import { IReactiveModel, ViewModel } from '@descomplica/microfrontend-mvvm';
import {
  action,
  computed,
  makeObservable,
  observable,
  runInAction,
} from 'mobx';

import { GetCoursePageLinkCollaboration } from '@/application/collaborations/get-course-page-link';
import { GetUserMicrocertificateCollaboration } from '@/application/collaborations/get-user-microcertificate/get-user-microcertificate.collaboration';
import { TrackEventCollaboration } from '@/application/collaborations/track-event.collaboration';
import { TrackPageViewCollaboration } from '@/application/collaborations/track-page-view.collaboration';
import {
  MicrocertificateCoursePageClicked,
  MicrocertificatePubliclyDownloaded,
} from '@/application/interfaces/event-tracker.interface';
import type { UserMicrocertificate } from '@/application/interfaces/user-microcertificate.interface';

export enum AvaliableFormats {
  PDF = 'pdf',
  PNG = 'png',
}

@ViewModel()
export class MicrocertificateViewModel implements IReactiveModel {
  @observable notFound = false;
  @observable microcertificateHash? = '';
  @observable userMicrocertificate: UserMicrocertificate | null;
  @observable coursePageLink? = '';

  constructor(
    private readonly trackEvent: TrackEventCollaboration,
    private readonly trackPageView: TrackPageViewCollaboration,
    private readonly getCoursePageLink: GetCoursePageLinkCollaboration,
    private readonly getUserMicrocertificate: GetUserMicrocertificateCollaboration,
  ) {
    makeObservable(this);
  }

  @action
  onInit(): void {
    this.trackPageView.run();
  }

  @action
  async setMicrocertificateHash(hash?: string): Promise<void> {
    if (!hash) return;

    runInAction(() => {
      this.microcertificateHash = hash;
    });

    const microcertificate = await this.getUserMicrocertificate.run(hash);

    if (!microcertificate) {
      runInAction(() => {
        this.notFound = true;
      });

      return;
    }

    const coursePageLink = await this.getCoursePageLink.run(
      microcertificate.course,
    );

    runInAction(() => {
      this.userMicrocertificate = microcertificate;
      this.coursePageLink = coursePageLink;
    });
  }

  @computed
  get microcertificatePreviewUrl(): string | undefined {
    if (!this.microcertificateHash) return undefined;

    return this.microcertificateUrlFor(
      this.microcertificateHash,
      AvaliableFormats.PNG,
    );
  }

  @action
  async downloadMicrocertificate(format: AvaliableFormats): Promise<void> {
    if (!this.microcertificateHash) return;

    const fileUrl = this.microcertificateUrlFor(
      this.microcertificateHash,
      format,
    );

    const fileName =
      format === AvaliableFormats.PNG
        ? `microcertificado_${this.microcertificateHash}.png`
        : `microcertificado_${this.microcertificateHash}.pdf`;

    this.trackEvent.run(
      new MicrocertificatePubliclyDownloaded(this.microcertificateHash, format),
    );

    await this.downloadFile(fileUrl, fileName);
  }

  @action
  trackNavigateToCoursePage(): void {
    if (!this.microcertificateHash) return;

    this.trackEvent.run(
      new MicrocertificateCoursePageClicked(
        this.microcertificateHash,
        this.userMicrocertificate?.course || 'Página da Faculdade',
      ),
    );
  }

  private microcertificateUrlFor(
    microcertificateHash: string,
    format: AvaliableFormats,
  ): string {
    const host = process.env.REACT_APP_MICROCERTIFICATES_PATH;

    const urls: { [key in AvaliableFormats]: string } = {
      pdf: `${host}/${microcertificateHash}.pdf`,
      png: `${host}/${microcertificateHash}.png`,
    };

    return urls[format];
  }

  private downloadFile(url: string, fileName: string): Promise<void> {
    return fetch(url, { method: 'get' })
      .then((res) => res.blob())
      .then((blob) => {
        const aElement = document.createElement('a');
        aElement.setAttribute('download', fileName);
        const href = URL.createObjectURL(blob);
        aElement.href = href;
        aElement.setAttribute('target', '_blank');
        aElement.click();
        URL.revokeObjectURL(href);
      })
      .catch(() => {
        // As fallback open the file in a new tab
        window.open(url, '_blank');
      });
  }
}
